/**
 * Accounts, contacts, programs, reference data, activity — §5.1, §5.2, §5.7.
 *
 * One file rather than eight, because these are ordinary CRUD shapes and
 * splitting them buys nothing. The shapes that carry real rules —
 * opportunities, closures, attachments — have their own files.
 */

import { z } from 'zod';
import {
  accountStatusSchema,
  actionOwnerTypeSchema,
  actionStatusSchema,
  attendeeTypeSchema,
  businessUnitCodeSchema,
  businessUnitStatusSchema,
  buyingRoleSchema,
  channelTypeSchema,
  dispositionSchema,
  endMarketSchema,
  firmTypeSchema,
  influenceLevelSchema,
  lifecycleStatusSchema,
  meetingTypeSchema,
  noteEntityTypeSchema,
  noteTypeSchema,
  prioritySchema,
  productFamilySchema,
  programStatusSchema,
  regionSchema,
  segmentSchema,
  sentimentSchema,
  technologyTypeSchema,
} from './enums.js';
import {
  countryCode,
  dateString,
  httpsUrl,
  ianaTimezone,
  longText,
  mediumText,
  shortText,
  timestampString,
  uuid,
} from './fields.js';
import { emailReminderModeSchema } from './users.js';

const requireSomething = <T extends z.ZodTypeAny>(schema: T) =>
  schema.refine(
    (b) => typeof b === 'object' && b !== null && Object.keys(b as object).length > 0,
    'an update must change something',
  );

// ---------------------------------------------------------------------------
// Accounts (§5.2)
// ---------------------------------------------------------------------------

export const createAccountSchema = z.object({
  name: z.string().min(1).max(300),
  parent_account_id: uuid.nullable().optional(),
  segment: segmentSchema,
  /**
   * `^https://` and `^[A-Z]{2}$` are check constraints on the table as well,
   * so a form that posts `example.com` or `us` earns a raw `23514` naming the
   * constraint. These two reject it first, with a field name — and the client
   * normalises before the write rather than relying on either.
   */
  website: httpsUrl.nullable().optional(),
  hq_country: countryCode.nullable().optional(),
  /** RF-0003: structured, because address is the POS files' only distinguishing datum. */
  address_line1: shortText.nullable().optional(),
  address_line2: shortText.nullable().optional(),
  city: shortText.nullable().optional(),
  state_region: shortText.nullable().optional(),
  postal_code: shortText.nullable().optional(),
  region: regionSchema,
  channel_type: channelTypeSchema.optional(),
  rep_firm_id: uuid.nullable().optional(),
  owner_user_id: uuid.nullable().optional(),
  nda_on_file: z.boolean().optional(),
  nda_expiry_date: dateString.nullable().optional(),
  status: accountStatusSchema.optional(),
  /** §8 item 6: set by a HUMAN, never derived from segment. */
  export_sensitive: z.boolean().optional(),
});

export const updateAccountSchema = requireSomething(createAccountSchema.partial());

// ---------------------------------------------------------------------------
// Contacts (§5.2)
//
// EXACTLY ONE of account_id / rep_firm_id. A distributor salesperson has no
// account parent at all, and those are precisely the contacts the importer
// creates — 33 distinct people in the real file — which is why §2 [R2] gives
// them their own authorization branch.
// ---------------------------------------------------------------------------

const contactCore = z.object({
  account_id: uuid.nullable().optional(),
  rep_firm_id: uuid.nullable().optional(),
  first_name: z.string().min(1).max(120),
  last_name: z.string().min(1).max(120),
  title: shortText.nullable().optional(),
  buying_role: buyingRoleSchema.optional(),
  email: z.string().email().max(320).nullable().optional(),
  phone: z.string().max(60).nullable().optional(),
  influence_level: influenceLevelSchema.optional(),
  disposition: dispositionSchema.optional(),
  is_primary: z.boolean().optional(),
  is_active: z.boolean().optional(),
});

export const createContactSchema = contactCore.refine(
  (b) => (b.account_id != null) !== (b.rep_firm_id != null),
  'exactly one of account_id and rep_firm_id',
);

export const updateContactSchema = requireSomething(contactCore.partial());

// ---------------------------------------------------------------------------
// Programs (§5.2)
// ---------------------------------------------------------------------------

export const createProgramSchema = z.object({
  account_id: uuid,
  name: z.string().min(1).max(300),
  description: mediumText.nullable().optional(),
  end_market: endMarketSchema.optional(),
  expected_sop_date: dateString.nullable().optional(),
  expected_program_years: z.number().int().min(1).max(30).nullable().optional(),
  annual_volume_estimate: z.number().int().min(0).nullable().optional(),
  status: programStatusSchema.optional(),
  export_sensitive: z.boolean().optional(),
});

export const updateProgramSchema = requireSomething(createProgramSchema.partial());

/**
 * `POST /api/programs/:id/cancel` — `Manager` or `Admin` only (§2 rule 3).
 *
 * A program spans accounts, so under the ordinary program write rule an
 * account owner could bulk-close opportunities owned by other reps on other
 * accounts: an escalation route around `canWriteOpportunity`.
 */
export const cancelProgramSchema = z.object({
  close_date: dateString.optional(),
  note: mediumText.optional(),
});

// ---------------------------------------------------------------------------
// Rep firms (§5.1) — Admin reference data
// ---------------------------------------------------------------------------

/**
 * **Admins enter the MARKUP percentage, never the factor** (§5.1).
 * `0.8695652` is a derived number nobody thinks in; the API takes `15` and
 * computes `1 / 1.15`.
 *
 * Read as a *margin* off resale, 15% would give `0.85`; read correctly as a
 * *markup* it gives `0.8696`. On the sample file that is $8,497,039 versus
 * $8,305,856 — a $191,183 gap. A 15% markup **is** a 13.04% margin: the same
 * fact in two vocabularies.
 */
export const createRepFirmSchema = z.object({
  name: z.string().min(1).max(300),
  firm_type: firmTypeSchema,
  region: regionSchema,
  primary_contact_name: shortText.nullable().optional(),
  email: z.string().email().max(320).nullable().optional(),
  /** Percent. `15` means a 15% markup, which is `net_factor = 1/1.15`. */
  markup_pct: z.number().min(0).max(1000).optional(),
  is_active: z.boolean().optional(),
});

export const updateRepFirmSchema = requireSomething(createRepFirmSchema.partial());

// ---------------------------------------------------------------------------
// Products and competitors (§5.1) — Admin reference data
// ---------------------------------------------------------------------------

export const createProductSchema = z.object({
  part_number: z.string().min(1).max(80),
  family: productFamilySchema,
  business_unit: businessUnitCodeSchema,
  description: shortText.nullable().optional(),
  package_type: shortText.nullable().optional(),
  lifecycle_status: lifecycleStatusSchema.optional(),
  datasheet_url: httpsUrl.nullable().optional(),
});

export const updateProductSchema = requireSomething(createProductSchema.partial());

export const createCompetitorSchema = z.object({
  name: z.string().min(1).max(200),
  technology_type: technologyTypeSchema,
  notes: mediumText.nullable().optional(),
});

export const updateCompetitorSchema = requireSomething(createCompetitorSchema.partial());

// ---------------------------------------------------------------------------
// Meetings and attendees (§5.7)
// ---------------------------------------------------------------------------

export const createMeetingSchema = z.object({
  subject: z.string().min(1).max(300),
  meeting_type: meetingTypeSchema.optional(),
  starts_at: timestampString,
  ends_at: timestampString.nullable().optional(),
  timezone: ianaTimezone.nullable().optional(),
  location: shortText.nullable().optional(),
  is_virtual: z.boolean().optional(),
  /** NOT NULL: a meeting always belongs to an account, even before it attaches to a deal. */
  account_id: uuid,
  opportunity_id: uuid.nullable().optional(),
  organizer_user_id: uuid.nullable().optional(),
  agenda: mediumText.nullable().optional(),
  notes: longText.nullable().optional(),
  sentiment: sentimentSchema.optional(),
  outcome: mediumText.nullable().optional(),
});

export const updateMeetingSchema = requireSomething(createMeetingSchema.partial());

const meetingAttendeeCore = z.object({
  meeting_id: uuid,
  attendee_type: attendeeTypeSchema,
  user_id: uuid.nullable().optional(),
  contact_id: uuid.nullable().optional(),
  free_text_name: shortText.nullable().optional(),
});

const exactlyOneAttendeeIdentity = (b: {
  user_id?: string | null;
  contact_id?: string | null;
  free_text_name?: string | null;
}): boolean =>
  [b.user_id, b.contact_id, b.free_text_name].filter((v) => v !== null && v !== undefined)
    .length === 1;

export const createMeetingAttendeeSchema = meetingAttendeeCore.refine(
  exactlyOneAttendeeIdentity,
  'exactly one of user_id, contact_id and free_text_name',
);

// ---------------------------------------------------------------------------
// Action items (§5.7, §8e)
// ---------------------------------------------------------------------------

const actionItemCore = z.object({
  title: z.string().min(1).max(300),
  description: mediumText.nullable().optional(),
  /** Nullable, and §8e [R3] depends on it: the digest gives these their own line. */
  opportunity_id: uuid.nullable().optional(),
  account_id: uuid.nullable().optional(),
  meeting_id: uuid.nullable().optional(),
  owner_type: actionOwnerTypeSchema.optional(),
  owner_user_id: uuid.nullable().optional(),
  owner_contact_id: uuid.nullable().optional(),
  due_date: dateString.nullable().optional(),
  priority: prioritySchema.optional(),
  status: actionStatusSchema.optional(),
  blocked_by_action_id: uuid.nullable().optional(),
  completion_note: mediumText.nullable().optional(),
});

export const createActionItemSchema = actionItemCore;
export const updateActionItemSchema = requireSomething(actionItemCore.partial());

/**
 * `POST /api/meetings/log` — a meeting, its attendees and its next-step
 * action items, written in **one transaction**.
 *
 * The meeting form used to make one request per row. A failure part-way left
 * a saved meeting behind a "not saved" message, and a retry duplicated it
 * (docs/bugs.md, 2026-10-10 session 1, part 2). The children carry no
 * `meeting_id` — the server sets it from the row it has just written — and
 * are strict, so a child naming some other meeting is refused rather than
 * silently re-pointed.
 */
export const logMeetingSchema = z.object({
  meeting: createMeetingSchema,
  attendees: z
    .array(
      meetingAttendeeCore
        .omit({ meeting_id: true })
        .strict()
        .refine(
          exactlyOneAttendeeIdentity,
          'exactly one of user_id, contact_id and free_text_name',
        ),
    )
    .max(200)
    .default([]),
  action_items: z
    .array(actionItemCore.omit({ meeting_id: true }).strict())
    .max(50)
    .default([]),
});
export type LogMeetingInput = z.input<typeof logMeetingSchema>;

// ---------------------------------------------------------------------------
// Notes (§5.7)
// ---------------------------------------------------------------------------

export const createNoteSchema = z.object({
  entity_type: noteEntityTypeSchema,
  entity_id: uuid,
  note_type: noteTypeSchema.optional(),
  body: longText.min(1),
  is_pinned: z.boolean().optional(),
});

export const updateNoteSchema = requireSomething(
  z.object({
    entity_type: noteEntityTypeSchema.optional(),
    entity_id: uuid.optional(),
    note_type: noteTypeSchema.optional(),
    body: longText.min(1).optional(),
    is_pinned: z.boolean().optional(),
  }),
);

// ---------------------------------------------------------------------------
// Admin: users, reassignment, erasure, settings (§2 rule 4, §8c)
// ---------------------------------------------------------------------------

/**
 * §2 rule 4: **`users.roles`, `entra_object_id` and `email` are written only by
 * JIT provisioning from token claims** and are rejected by every admin
 * endpoint. Roles are assigned in Entra and nowhere else. They are absent from
 * this schema, and the route rejects them by name rather than stripping them
 * silently — an Admin who thinks they granted a role and did not is worse off
 * than one who got an error.
 */
export const updateUserSchema = requireSomething(
  z.object({
    is_active: z.boolean().optional(),
    manager_user_id: uuid.nullable().optional(),
    title: shortText.nullable().optional(),
    timezone: ianaTimezone.optional(),
    email_reminders: emailReminderModeSchema.optional(),
  }),
);

export const REJECTED_USER_FIELDS = ['roles', 'entra_object_id', 'email'] as const;

/** §8c: deactivation is refused while the user owns open records, unless a target is supplied. */
export const reassignUserSchema = z.object({
  to_user_id: uuid,
  note: mediumText.optional(),
});

export const eraseContactSchema = z.object({
  reason: shortText,
});

export const updateAppSettingSchema = z.object({
  value: z.unknown(),
  description: shortText.nullable().optional(),
});

export const updateRefusalTextSchema = z.object({
  text: longText.min(1),
});

// ---------------------------------------------------------------------------
// Global search (§9: term in the BODY)
// ---------------------------------------------------------------------------

export const globalSearchSchema = z.object({
  term: z.string().min(1).max(200),
  limit: z.number().int().min(1).max(50).optional(),
});

export const globalSearchHitSchema = z.object({
  kind: z.enum(['opportunity', 'account', 'contact', 'program', 'note']),
  id: uuid,
  label: z.string(),
  sublabel: z.string().nullable(),
  /** Where the client should navigate. Never a free-text query string (§9). */
  href: z.string(),
});

export type GlobalSearchHit = z.infer<typeof globalSearchHitSchema>;

// ---------------------------------------------------------------------------
// Business units (session 7) — rows of the `business_units` table
// ---------------------------------------------------------------------------

export const businessUnitSchema = z.object({
  code: businessUnitCodeSchema,
  label: z.string(),
  sort_order: z.number().int(),
  status: businessUnitStatusSchema,
});
export type BusinessUnit = z.infer<typeof businessUnitSchema>;

// ---------------------------------------------------------------------------
// /api/reference — everything the client caches, in ONE call
// ---------------------------------------------------------------------------

export const referenceDataSchema = z.object({
  stages: z.array(
    z.object({
      code: z.string(),
      name: z.string(),
      channel_aliases: z.array(z.string()),
      sort_order: z.number().int(),
      default_probability: z.number().int(),
      entry_criteria: z.string().nullable(),
      required_fields: z.array(z.string()),
      target_days: z.number().int().nullable(),
    }),
  ),
  reason_codes: z.array(
    z.object({
      code: z.string(),
      label: z.string(),
      category: z.string(),
      applies_to: z.array(z.string()),
      is_active: z.boolean(),
    }),
  ),
  /**
   * Session 7: the one BU list, in `sort_order`, retired rows included so
   * history can still label them. Filters and forms offer the non-retired ones.
   */
  business_units: z.array(businessUnitSchema),
  products: z.array(
    z.object({
      id: uuid,
      part_number: z.string(),
      family: productFamilySchema,
      business_unit: businessUnitCodeSchema,
      lifecycle_status: lifecycleStatusSchema,
      description: z.string().nullable(),
    }),
  ),
  competitors: z.array(
    z.object({ id: uuid, name: z.string(), technology_type: technologyTypeSchema }),
  ),
  rep_firms: z.array(
    z.object({
      id: uuid,
      name: z.string(),
      firm_type: firmTypeSchema,
      region: regionSchema,
      /** Both, always: nobody thinks in the factor and everybody argues about the percent. */
      net_factor: z.string(),
      markup_pct: z.number(),
      is_active: z.boolean(),
    }),
  ),
  regions: z.array(regionSchema),
  users: z.array(
    z.object({
      id: uuid,
      display_name: z.string(),
      email: z.string(),
      is_active: z.boolean(),
      roles: z.array(z.string()),
    }),
  ),
  /** §8 item 6: the pre-selection lists, so the form can default the checkbox. */
  export_sensitive_accounts: z.array(uuid),
  export_sensitive_programs: z.array(uuid),
  export_policy_version: z.string(),
  attachment_max_bytes: z.number().int(),
  attachment_content_type_allowlist: z.array(z.string()),
  multipart_threshold_bytes: z.number().int(),
});

export type ReferenceData = z.infer<typeof referenceDataSchema>;
